import { getNextStaticProps } from "@faustjs/next";
import { client, PageIdType } from "../client";
import { GetStaticPropsContext } from "next";

import { useMenu } from "hooks";
import Layout from "layouts/main";
import {
	FullBackgroundHero,
	PainPointsSection,
	CTASection,
	SocialProofSection,
	ProductSlideshowSection,
	FitSection,
	RiskVsValueSection,
	DifferentiatorsSection,
	BuildSomethingSection,
	SimpleCTA,
	Capabilities,
} from "components";
import { Cog8ToothIcon as CogIcon, EyeIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import HeadingBlock from "components/atoms/blocks/HeadingBlock";

export default function Page() {
	const { useQuery } = client;
	const { usePage } = client;
	const headerLinks = useMenu("PRIMARY");
	const footerLinks = useMenu("FOOTER");
	const page = usePage({
		id: `/for-product-marketing-teams`,
		idType: PageIdType.URI,
	});

	return (
		<Layout
			page={page}
			headerLinks={headerLinks}
			footerLinks={footerLinks}
			transparentHeader
			lightLogo
		>
			<FullBackgroundHero
				backgroundImage="/images/crafted-office-background.jpg"
				headline="Build software people love, faster."
				bracketedText="faster."
				bracketAnimateColors={true}
				subheadline="Your dedicated development partner for high-impact digital products."
				features={[
					{
						icon: EyeIcon,
						text: "Beautiful UX",
						iconColor: "bg-green",
					},
					{
						icon: CogIcon,
						text: "World Class Engineers",
						iconColor: "bg-pink",
					},
					{
						icon: ChartBarIcon,
						text: "AI-Accelerated Velocity",
						iconColor: "bg-yellow",
					},
				]}
				ctaText="Let's Talk"
				showColorfulLine={true}
			/>

			<div className="container mx-auto px-2 xl:px-0 pt-20 max-w-6xl">
				<h2 className="text-4xl md:text-5xl text-left mb-8">
					Delivering Software Users Love <br />
					Is A Lot Harder Than It Looks…
				</h2>
			</div>
			<PainPointsSection />
			<SimpleCTA
				title="The job hasn't changed: <span class='italic'>build exceptional software</span>. But the pressure to deliver fast has never been higher."
				bracketedText="deliver fast"
				bracketAnimateColors={true}
				ctaText="Let's Talk"
				ctaLink="/contact"
			/>
			<FitSection />
			<Capabilities />
			<SocialProofSection
				sectionTitle="The Partner Behind Products Used by Millions"
				caseStudy={{
					label: "Case Study Highlight:",
					title:
						"A national brand needed to redesign and rebuild a complex customer experience — fast.",
					description:
						"Crafted integrated with their product team, re-architected the UI, increased performance, and delivered a launch-ready product far ahead of internal projections.",
				}}
				testimonial={{
					quote:
						"Crafted operates like a world-class internal team. Fast, thoughtful, reliable — and every deliverable just feels right.",
					title: "Director of Digital Experience",
					company: "USA Football",
				}}
				additionalText="+ high-growth startups and enterprise digital teams across the U.S."
				logos={[
					{ src: "/images/republic.png", alt: "Republic Airways" },
					{ src: "/images/CrewCarwash.png", alt: "Crew Carwash" },
					{ src: "/images/USA.png", alt: "USA Football" },
					{ src: "/images/MISO.png", alt: "MISO Energy" },
				]}
			/>
			<ProductSlideshowSection />
			<div className="container mx-auto py-[clamp(40px,6.25vw,80px)] max-w-6xl">
				<HeadingBlock
					content="Partnering With Crafted Is <br />Lower Risk Than Going It Alone"
					level={2}
					textAlign="left"
				/>
				<RiskVsValueSection />
				<SimpleCTA
					title="The real risk <span className='italic'>isn't partnering</span>. It's missing your window to deliver."
					bracketedText="missing your window to deliver."
					bracketColor="blue"
					ctaText="Let's Talk"
					ctaLink="/contact"
				/>
			</div>
			<DifferentiatorsSection
				title="Why Product & Technology Teams Choose Crafted — And Stay for the Long Run"
				rows={[
					[
						{
							title: "Deep & Broad Expertise",
							description:
								"We've solved the hardest backend problems and crafted the most polished frontend experiences — often in the same week.",
							bgColor: "bg-purple-50",
						},
						{
							title: "Disciplined, Professional Execution",
							description:
								"Weekly reports. Clear budgets. Predictable delivery. A culture built on accountability and ownership.",
							bgColor: "bg-teal-50",
						},
					],
					[
						{
							title: "Refined Taste",
							description:
								"Most engineering teams can ship. Few can ship beautiful, intuitive, user-loved products. Crafted is built on creative empathy and product instinct.",
							bgColor: "bg-red-50",
						},
						{
							title: "AI-Native Velocity",
							description:
								"We don't \"sell AI.\" We build with it — unlocking a level of speed, iteration, and engineering leverage traditional teams can't match.",
							bgColor: "bg-amber-50",
						},
						{
							title: "A True Partnership Model",
							description:
								"Not a vendor. Not freelancers. Not a fixed-bid factory. A long-term product & engineering engine you can rely on every day.",
							bgColor: "bg-blue-50",
						},
					],
				]}
			/>
			<CTASection
				images={[
					{ src: "/slide-show/For Product Teams/AllIn.jpg", alt: "All In" },
					{ src: "/slide-show/For Product Teams/Fastlane.jpg", alt: "Fastlane" },
					{ src: "/slide-show/For Product Teams/Lensquote.jpg", alt: "Lensquote" },
					{ src: "/slide-show/For Product Teams/MISOGateway.jpg", alt: "MISO Gateway" },
					{ src: "/slide-show/For Product Teams/MISOLMPContourMap.jpg", alt: "MISO LMP Contour Map" },
					{ src: "/slide-show/For Product Teams/MedRep.jpg", alt: "Med Rep" },
					{ src: "/slide-show/For Product Teams/Nexsyis.jpg", alt: "Nexsyis" },
					{ src: "/slide-show/For Product Teams/usafbDashboard.jpg", alt: "USAFB Dashboard" },
					{ src: "/slide-show/For Product Teams/usafbLeagueFinder.jpg", alt: "USAFB League Finder" },
					{ src: "/slide-show/For Product Teams/yojo.jpg", alt: "Yojo" },
				]}
			/>
		</Layout>
	);
}

export async function getStaticProps(context: GetStaticPropsContext) {
	return getNextStaticProps(context, {
		Page,
		client,
	});
}
